// src/pages/admin/SubscriptionInquiries.jsx - Enhanced with Advanced Filters
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FiCalendar,
  FiCheckCircle,
  FiArchive,
  FiTrash2,
  FiStar,
  FiSearch,
  FiRefreshCw,
  FiUser,
  FiMail,
  FiEye,
  FiMessageSquare,
  FiDollarSign,
  FiClock,
  FiSend,
  FiAlertCircle,
  FiX,
  FiTrendingUp,
  FiFilter,
  FiGrid,
  FiList,
  FiChevronDown,
  FiChevronUp,
  FiBarChart2,
  FiPieChart,
  FiDownload,
  FiCheck,
  FiSquare,
  FiFileText,
  FiTag,
  FiAtSign,
  FiLoader,
} from 'react-icons/fi';
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import subscriptionService from '../../services/subscriptionService';
import SubscriptionInquiryModal from '../../components/admin/SubscriptionInquiryModal';

// Custom Tooltip for Charts
const CustomTooltip = ({ active, payload, label, isDark }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="p-3 rounded-lg shadow-lg border"
        style={{
          backgroundColor: isDark ? '#1f2937' : '#ffffff',
          borderColor: isDark ? '#374151' : '#e5e7eb',
        }}
      >
        <p
          className="text-sm font-semibold"
          style={{ color: isDark ? '#f1f5f9' : '#1e293b' }}
        >
          {label}
        </p>
        <p
          className="text-xs mt-1"
          style={{ color: isDark ? '#94a3b8' : '#64748b' }}
        >
          Inquiries:{' '}
          <span className="font-bold" style={{ color: '#8b5cf6' }}>
            {payload[0].value}
          </span>
        </p>
      </div>
    );
  }
  return null;
};

// Custom Label for Pie Chart
const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  index,
  name,
  value,
  isDark,
}) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill={isDark ? '#f1f5f9' : '#1e293b'}
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      className="text-xs font-medium"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

function SubscriptionInquiries() {
  const { actualTheme } = useTheme();
  const { currentUser } = useAuth();
  const isDark = actualTheme === 'dark';

  // ==================== STATE ====================
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [billingFilter, setBillingFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inReview: 0,
    responded: 0,
    approved: 0,
    declined: 0,
    completed: 0,
    archived: 0,
    monthly: 0,
    yearly: 0,
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [timelineData, setTimelineData] = useState([]);
  const [timelineRange, setTimelineRange] = useState('30days');
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [planDistribution, setPlanDistribution] = useState({});
  const [dateRange, setDateRange] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [availablePlans, setAvailablePlans] = useState([]);
  const itemsPerPage = 20;

  // ==================== CHART COLORS ====================
  const chartColors = useMemo(
    () => ({
      primary: '#8b5cf6',
      primaryLight: '#a78bfa',
      secondary: '#ec4899',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6',
      cyan: '#06b6d4',
      purple: '#a855f7',
      text: isDark ? '#f1f5f9' : '#1e293b',
      textSecondary: isDark ? '#94a3b8' : '#64748b',
      grid: isDark ? '#334155' : '#e2e8f0',
      tooltipBg: isDark ? '#1e293b' : '#ffffff',
      tooltipBorder: isDark ? '#334155' : '#e2e8f0',
    }),
    [isDark],
  );

  const PIE_COLORS = [
    chartColors.primary,
    chartColors.secondary,
    chartColors.warning,
    chartColors.success,
    chartColors.cyan,
    chartColors.purple,
    chartColors.info,
    chartColors.error,
  ];

  // ==================== LOAD DATA ====================
  const loadInquiries = useCallback(async () => {
    try {
      setLoading(true);
      const result = await subscriptionService.getInquiries({}, null, 100);

      let inquiriesList = result.inquiries || [];

      // Apply status filter
      if (statusFilter !== 'all') {
        inquiriesList = inquiriesList.filter(i => i.status === statusFilter);
      }

      // Apply billing filter
      if (billingFilter !== 'all') {
        inquiriesList = inquiriesList.filter(
          i => i.planData?.billingCycle === billingFilter,
        );
      }

      // Apply plan filter
      if (planFilter !== 'all') {
        inquiriesList = inquiriesList.filter(
          i => i.planData?.name === planFilter,
        );
      }

      // Apply date range filter
      if (dateRange !== 'all') {
        const now = new Date();
        const cutoff = new Date();
        if (dateRange === 'today') cutoff.setHours(0, 0, 0, 0);
        else if (dateRange === 'week') cutoff.setDate(now.getDate() - 7);
        else if (dateRange === 'month') cutoff.setMonth(now.getMonth() - 1);
        else if (dateRange === 'quarter') cutoff.setMonth(now.getMonth() - 3);

        inquiriesList = inquiriesList.filter(i => {
          const date = i.createdAt?.toDate?.() || new Date(i.createdAt);
          return date >= cutoff;
        });
      }

      // Apply search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        inquiriesList = inquiriesList.filter(
          i =>
            i.name?.toLowerCase().includes(term) ||
            i.email?.toLowerCase().includes(term) ||
            i.company?.toLowerCase().includes(term) ||
            i.planData?.name?.toLowerCase().includes(term) ||
            i.message?.toLowerCase().includes(term) ||
            i.status?.toLowerCase().includes(term) ||
            i.planData?.billingCycle?.toLowerCase().includes(term),
        );
      }

      // Apply sorting
      inquiriesList.sort((a, b) => {
        let aVal, bVal;
        switch (sortBy) {
          case 'date':
            aVal = a.createdAt?.toDate?.() || new Date(a.createdAt);
            bVal = b.createdAt?.toDate?.() || new Date(b.createdAt);
            break;
          case 'name':
            aVal = a.name || '';
            bVal = b.name || '';
            break;
          case 'plan':
            aVal = a.planData?.name || '';
            bVal = b.planData?.name || '';
            break;
          case 'status':
            aVal = a.status || '';
            bVal = b.status || '';
            break;
          case 'price':
            aVal = parseFloat(a.planData?.price) || 0;
            bVal = parseFloat(b.planData?.price) || 0;
            break;
          default:
            aVal = a.createdAt?.toDate?.() || new Date(a.createdAt);
            bVal = b.createdAt?.toDate?.() || new Date(b.createdAt);
        }
        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });

      setInquiries(inquiriesList);
      setHasMore(false);

      // Load statistics
      const statsData = await subscriptionService.getStats();
      setStats(statsData);

      // Calculate plan distribution
      const planMap = {};
      const planNames = new Set();
      inquiriesList.forEach(inquiry => {
        const planName = inquiry.planData?.name || 'Unknown Plan';
        planMap[planName] = (planMap[planName] || 0) + 1;
        planNames.add(planName);
      });
      setPlanDistribution(planMap);
      setAvailablePlans([...planNames]);

      // Load timeline data
      let days = 30;
      if (timelineRange === '90days') days = 90;
      if (timelineRange === 'lifetime') days = 365 * 5;
      const timeline = await subscriptionService.getTimelineData(days);
      setTimelineData(timeline);
    } catch (error) {
      console.error('Error loading subscription inquiries:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [
    statusFilter,
    billingFilter,
    planFilter,
    dateRange,
    searchTerm,
    sortBy,
    sortOrder,
    timelineRange,
  ]);

  useEffect(() => {
    loadInquiries();
  }, [
    statusFilter,
    billingFilter,
    planFilter,
    dateRange,
    sortBy,
    sortOrder,
    timelineRange,
  ]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadInquiries();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadInquiries();
  };

  // ==================== EXPORT ====================
  const handleExport = (format = 'csv') => {
    const data = inquiries.map(inquiry => ({
      Name: inquiry.name,
      Email: inquiry.email,
      Company: inquiry.company || '',
      Plan: inquiry.planData?.name || 'Unknown',
      'Billing Cycle':
        inquiry.planData?.billingCycle === 'monthly' ? 'Monthly' : 'Yearly',
      Price: inquiry.planData?.price ? `$${inquiry.planData.price}` : 'N/A',
      Status: inquiry.status,
      'Submitted At': inquiry.createdAt?.toDate?.().toLocaleString() || '',
      Message: inquiry.message,
      'Has Notes': inquiry.notes?.length > 0 ? 'Yes' : 'No',
      'Notes Count': inquiry.notes?.length || 0,
      'Email Response Sent': inquiry.emailResponseSent ? 'Yes' : 'No',
      Features: inquiry.planData?.features?.join(', ') || '',
    }));

    if (format === 'csv') {
      const headers = Object.keys(data[0] || {});
      const csv = [
        headers.join(','),
        ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(',')),
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `subscription-inquiries-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else if (format === 'json') {
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `subscription-inquiries-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  // ==================== INQUIRY ACTIONS ====================
  const handleViewInquiry = async inquiry => {
    setSelectedInquiry(inquiry);
    setIsModalOpen(true);

    if (!inquiry.isRead) {
      await subscriptionService.markAsRead(inquiry.id, currentUser?.uid);
      setInquiries(prev =>
        prev.map(i => (i.id === inquiry.id ? { ...i, isRead: true } : i)),
      );
    }
  };

  const handleStatusUpdate = async (id, status) => {
    await subscriptionService.updateStatus(id, status, currentUser?.uid);
    setInquiries(prev => prev.map(i => (i.id === id ? { ...i, status } : i)));
    const statsData = await subscriptionService.getStats();
    setStats(statsData);
    if (selectedInquiry?.id === id) {
      setSelectedInquiry(prev => ({ ...prev, status }));
    }
  };

  const handleArchive = async (id, isArchived) => {
    await subscriptionService.toggleArchive(id, isArchived);
    setInquiries(prev =>
      prev.map(i => (i.id === id ? { ...i, isArchived } : i)),
    );
    const statsData = await subscriptionService.getStats();
    setStats(statsData);
    if (selectedInquiry?.id === id) {
      setSelectedInquiry(prev => ({ ...prev, isArchived }));
    }
  };

  const handleDelete = async id => {
    const inquiry = inquiries.find(i => i.id === id);
    const notesCount = inquiry?.notes?.length || 0;
    const responsesCount = inquiry?.responses?.length || 0;
    const notificationsCount = inquiry?.notificationIds?.length || 0;

    const confirmMessage = `Are you sure you want to delete this inquiry?

This will permanently remove:
• The inquiry itself
• ${notesCount} note(s)
• ${responsesCount} response(s) 
• ${notificationsCount} notification(s)

This action cannot be undone!`;

    if (!window.confirm(confirmMessage)) return;

    try {
      const result = await subscriptionService.deleteInquiry(id);
      console.log('Delete result:', result);

      setInquiries(prev => prev.filter(i => i.id !== id));
      const statsData = await subscriptionService.getStats();
      setStats(statsData);
      if (selectedInquiry?.id === id) {
        setIsModalOpen(false);
        setSelectedInquiry(null);
      }
      setSelectedIds(prev => prev.filter(sid => sid !== id));

      if (result.notifications > 0) {
        alert(`✅ Inquiry deleted successfully!
• Removed ${result.notes} note(s)
• Removed ${result.responses} response(s)
• Removed ${result.notifications} notification(s)`);
      }
    } catch (error) {
      console.error('Error deleting inquiry:', error);
      alert('Failed to delete inquiry. Please try again.');
    }
  };

  // ==================== BULK SELECTION ====================
  const toggleSelect = id => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id],
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(inquiries.map(i => i.id));
    }
    setSelectAll(!selectAll);
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectAll(false);
  };

  // ==================== BULK DELETE ====================
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    let totalNotes = 0;
    let totalResponses = 0;
    let totalNotifications = 0;

    selectedIds.forEach(id => {
      const inquiry = inquiries.find(i => i.id === id);
      if (inquiry) {
        totalNotes += inquiry.notes?.length || 0;
        totalResponses += inquiry.responses?.length || 0;
        totalNotifications += inquiry.notificationIds?.length || 0;
      }
    });

    const confirmMessage = `Are you sure you want to delete ${selectedIds.length} selected inquiry(ies)?

This will permanently remove:
• ${selectedIds.length} inquiry(ies)
• ${totalNotes} note(s)
• ${totalResponses} response(s)
• ${totalNotifications} notification(s)

This action cannot be undone!`;

    if (!window.confirm(confirmMessage)) return;

    try {
      const result = await subscriptionService.bulkAction(
        selectedIds,
        'delete',
      );
      console.log('Bulk delete result:', result);

      if (result.success) {
        alert(`✅ ${result.deletedCount} inquiry(ies) deleted successfully!
${result.failedCount > 0 ? `⚠️ ${result.failedCount} failed to delete` : ''}`);
      }

      clearSelection();
      loadInquiries();
    } catch (error) {
      console.error('Error in bulk delete:', error);
      alert('Failed to delete selected inquiries');
    }
  };

  const handleBulkAction = async (action, value = null) => {
    if (!selectedIds?.length) return;

    const requiresConfirmation = action === 'delete';
    if (
      requiresConfirmation &&
      !window.confirm(
        `Are you sure you want to ${action} ${selectedIds.length} inquiry(ies)?`,
      )
    ) {
      return;
    }

    try {
      await subscriptionService.bulkAction(selectedIds, action, value);

      if (action === 'delete') {
        setInquiries(prev => prev.filter(i => !selectedIds.includes(i.id)));
        clearSelection();
      } else if (action === 'archive') {
        setInquiries(prev =>
          prev.map(i =>
            selectedIds.includes(i.id) ? { ...i, isArchived: true } : i,
          ),
        );
        clearSelection();
      } else if (action === 'unarchive') {
        setInquiries(prev =>
          prev.map(i =>
            selectedIds.includes(i.id) ? { ...i, isArchived: false } : i,
          ),
        );
        clearSelection();
      } else if (action === 'mark-read') {
        setInquiries(prev =>
          prev.map(i =>
            selectedIds.includes(i.id) ? { ...i, isRead: true } : i,
          ),
        );
        clearSelection();
      } else if (action === 'set-status' && value) {
        setInquiries(prev =>
          prev.map(i =>
            selectedIds.includes(i.id) ? { ...i, status: value } : i,
          ),
        );
        clearSelection();
      }

      const statsData = await subscriptionService.getStats();
      setStats(statsData);
      console.log(
        `✅ Bulk action "${action}" completed on ${selectedIds.length} items`,
      );
    } catch (error) {
      console.error('Bulk action error:', error);
      alert(`Failed to ${action} selected items. Please try again.`);
    }
  };

  // ==================== UTILITY FUNCTIONS ====================
  const getStatusBadge = status => {
    const badges = {
      pending: { label: 'Pending', color: '#eab308', icon: FiClock },
      'in-review': { label: 'In Review', color: '#3b82f6', icon: FiEye },
      responded: {
        label: 'Responded',
        color: '#8b5cf6',
        icon: FiMessageSquare,
      },
      approved: { label: 'Approved', color: '#10b981', icon: FiCheckCircle },
      declined: { label: 'Declined', color: '#ef4444', icon: FiAlertCircle },
      completed: { label: 'Completed', color: '#059669', icon: FiCheckCircle },
    };
    return badges[status] || badges.pending;
  };

  const filters = [
    { id: 'all', label: 'All', count: stats.total, icon: FiCalendar },
    { id: 'pending', label: 'Pending', count: stats.pending, icon: FiClock },
    { id: 'in-review', label: 'In Review', count: stats.inReview, icon: FiEye },
    {
      id: 'responded',
      label: 'Responded',
      count: stats.responded,
      icon: FiMessageSquare,
    },
    {
      id: 'approved',
      label: 'Approved',
      count: stats.approved,
      icon: FiCheckCircle,
    },
    {
      id: 'declined',
      label: 'Declined',
      count: stats.declined,
      icon: FiAlertCircle,
    },
    {
      id: 'completed',
      label: 'Completed',
      count: stats.completed,
      icon: FiCheckCircle,
    },
    {
      id: 'archived',
      label: 'Archived',
      count: stats.archived,
      icon: FiArchive,
    },
  ];

  const billingOptions = [
    { id: 'all', label: 'All Cycles' },
    { id: 'monthly', label: 'Monthly', color: '#8b5cf6' },
    { id: 'yearly', label: 'Yearly', color: '#10b981' },
  ];

  const formatDate = timestamp => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString();
    } catch {
      return 'Invalid Date';
    }
  };

  const formatPrice = planData => {
    if (!planData) return 'N/A';
    if (planData.monthlyPrice && planData.yearlyPrice) {
      return `$${planData.monthlyPrice}/mo • $${planData.yearlyPrice}/yr`;
    }
    if (planData.price) {
      const cycle = planData.billingCycle === 'monthly' ? 'mo' : 'yr';
      return `$${planData.price}/${cycle}`;
    }
    return 'Custom pricing';
  };

  // ==================== CHART DATA ====================
  const chartData = useMemo(() => {
    return timelineData.map(item => ({
      date: item.date,
      count: item.count,
      name: new Date(item.date).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      }),
    }));
  }, [timelineData]);

  const planChartData = useMemo(() => {
    return Object.entries(planDistribution)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [planDistribution]);

  const billingChartData = useMemo(() => {
    const monthly = stats.monthly || 0;
    const yearly = stats.yearly || 0;
    return [
      { name: 'Monthly', value: monthly, color: chartColors.primary },
      { name: 'Yearly', value: yearly, color: chartColors.success },
    ].filter(item => item.value > 0);
  }, [stats, chartColors]);

  const statusChartData = useMemo(() => {
    return [
      { name: 'Pending', value: stats.pending, color: '#eab308' },
      { name: 'In Review', value: stats.inReview, color: '#3b82f6' },
      { name: 'Responded', value: stats.responded, color: '#8b5cf6' },
      { name: 'Approved', value: stats.approved, color: '#10b981' },
      { name: 'Declined', value: stats.declined, color: '#ef4444' },
      { name: 'Completed', value: stats.completed, color: '#059669' },
    ].filter(item => item.value > 0);
  }, [stats]);

  // ==================== INQUIRY CARD ====================
  const InquiryCard = ({ inquiry }) => {
    const statusBadge = getStatusBadge(inquiry.status);
    const StatusIcon = statusBadge.icon;
    const isSelected = selectedIds.includes(inquiry.id);
    const planData = inquiry.planData;

    return (
      <div
        className={`group rounded-xl border transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
          isSelected ? 'ring-2 ring-purple-500' : ''
        } ${!inquiry.isRead ? 'border-l-4' : ''}`}
        style={{
          backgroundColor: 'var(--bg)',
          borderColor: 'var(--border)',
          borderLeftColor: !inquiry.isRead ? '#3b82f6' : 'var(--border)',
        }}
      >
        <div className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleSelect(inquiry.id)}
                className="w-4 h-4 rounded"
                style={{ accentColor: 'var(--text)' }}
              />
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--border)' }}
              >
                <FiUser className="w-5 h-5" style={{ color: 'var(--text)' }} />
              </div>
            </div>
            <span
              className="px-2 py-0.5 rounded-full text-xs flex items-center gap-1"
              style={{
                backgroundColor:
                  planData?.billingCycle === 'yearly'
                    ? '#10b98115'
                    : '#8b5cf615',
                color:
                  planData?.billingCycle === 'yearly' ? '#10b981' : '#8b5cf6',
              }}
            >
              <FiCalendar className="w-2 h-2" />
              {planData?.billingCycle === 'monthly' ? 'Monthly' : 'Yearly'}
            </span>
          </div>

          <div className="mb-3">
            <h3
              className={`${!inquiry.isRead ? 'font-bold' : 'font-semibold'} text-base`}
              style={{ color: 'var(--text)' }}
            >
              {inquiry.name}
            </h3>
            {inquiry.company && (
              <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>
                {inquiry.company}
              </p>
            )}
          </div>

          <div className="mb-3">
            <div className="flex items-center gap-1 mb-1">
              <FiStar
                className="w-3 h-3"
                style={{ color: chartColors.primary }}
              />
              <span
                className="text-xs font-medium"
                style={{ color: 'var(--text)' }}
              >
                Plan:
              </span>
              <span className="text-xs" style={{ color: 'var(--muted)' }}>
                {planData?.name || 'Unknown'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <FiDollarSign
                className="w-3 h-3"
                style={{ color: chartColors.success }}
              />
              <span
                className="text-xs font-medium"
                style={{ color: 'var(--text)' }}
              >
                Price:
              </span>
              <span className="text-xs" style={{ color: 'var(--muted)' }}>
                {formatPrice(planData)}
              </span>
            </div>
            {inquiry.notes?.length > 0 && (
              <div className="flex items-center gap-1 mt-1">
                <FiFileText
                  className="w-3 h-3"
                  style={{ color: chartColors.cyan }}
                />
                <span className="text-xs" style={{ color: 'var(--muted)' }}>
                  {inquiry.notes.length} note(s)
                </span>
              </div>
            )}
          </div>

          <p
            className="text-sm line-clamp-2 mb-3"
            style={{ color: 'var(--muted)' }}
          >
            {inquiry.message}
          </p>

          <div
            className="flex items-center justify-between pt-3 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <div className="flex items-center gap-1">
              <span
                className="px-2 py-0.5 rounded-full text-xs flex items-center gap-1"
                style={{
                  backgroundColor: `${statusBadge.color}15`,
                  color: statusBadge.color,
                }}
              >
                <StatusIcon className="w-2 h-2" />
                {statusBadge.label}
              </span>
              <span
                className="text-xs flex items-center gap-1"
                style={{ color: 'var(--muted)' }}
              >
                <FiCalendar className="w-3 h-3" />
                {formatDate(inquiry.createdAt)}
              </span>
            </div>
            <button
              onClick={() => handleViewInquiry(inquiry)}
              className="p-1.5 rounded-lg transition-colors hover:bg-black/5 dark:hover:bg-white/5"
            >
              <FiEye className="w-4 h-4" style={{ color: 'var(--muted)' }} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ==================== INQUIRY LIST ITEM ====================
  const InquiryListItem = ({ inquiry }) => {
    const statusBadge = getStatusBadge(inquiry.status);
    const StatusIcon = statusBadge.icon;
    const isSelected = selectedIds.includes(inquiry.id);
    const planData = inquiry.planData;

    return (
      <div
        className={`p-4 rounded-xl border transition-all duration-200 hover:shadow-md ${
          isSelected ? 'ring-2 ring-purple-500' : ''
        } ${!inquiry.isRead ? 'border-l-4' : ''}`}
        style={{
          backgroundColor: 'var(--bg)',
          borderColor: 'var(--border)',
          borderLeftColor: !inquiry.isRead ? '#3b82f6' : 'var(--border)',
        }}
      >
        <div className="flex items-start gap-4">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleSelect(inquiry.id)}
              className="mt-1 w-4 h-4 rounded"
              style={{ accentColor: 'var(--text)' }}
            />
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <FiUser className="w-5 h-5" style={{ color: 'var(--text)' }} />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3
                  className={`${!inquiry.isRead ? 'font-bold' : 'font-semibold'}`}
                  style={{ color: 'var(--text)' }}
                >
                  {inquiry.name}
                </h3>
                {inquiry.company && (
                  <>
                    <span style={{ color: 'var(--muted)' }}>•</span>
                    <span className="text-sm" style={{ color: 'var(--muted)' }}>
                      {inquiry.company}
                    </span>
                  </>
                )}
                <span
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor:
                      planData?.billingCycle === 'yearly'
                        ? '#10b98115'
                        : '#8b5cf615',
                    color:
                      planData?.billingCycle === 'yearly'
                        ? '#10b981'
                        : '#8b5cf6',
                  }}
                >
                  {planData?.billingCycle === 'monthly' ? 'Monthly' : 'Yearly'}
                </span>
                {inquiry.notes?.length > 0 && (
                  <span
                    className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--muted)',
                    }}
                  >
                    <FiFileText className="w-3 h-3" />
                    {inquiry.notes.length}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="px-2 py-0.5 rounded-full text-xs flex items-center gap-1"
                  style={{
                    backgroundColor: `${statusBadge.color}15`,
                    color: statusBadge.color,
                  }}
                >
                  <StatusIcon className="w-2 h-2" />
                  {statusBadge.label}
                </span>
                <span
                  className="text-xs flex items-center gap-1"
                  style={{ color: 'var(--muted)' }}
                >
                  <FiCalendar className="w-3 h-3" />
                  {formatDate(inquiry.createdAt)}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mb-2">
              <span
                className="text-xs flex items-center gap-1"
                style={{ color: 'var(--muted)' }}
              >
                <FiStar
                  className="w-3 h-3"
                  style={{ color: chartColors.primary }}
                />
                Plan:{' '}
                <span className="font-medium" style={{ color: 'var(--text)' }}>
                  {planData?.name || 'Unknown'}
                </span>
              </span>
              <span
                className="text-xs flex items-center gap-1"
                style={{ color: 'var(--muted)' }}
              >
                <FiDollarSign
                  className="w-3 h-3"
                  style={{ color: chartColors.success }}
                />
                Price:{' '}
                <span className="font-medium" style={{ color: 'var(--text)' }}>
                  {formatPrice(planData)}
                </span>
              </span>
            </div>

            <p
              className="text-sm line-clamp-2 mb-3"
              style={{ color: 'var(--muted)' }}
            >
              {inquiry.message}
            </p>

            {planData?.features && planData.features.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {planData.features.slice(0, 3).map((feature, idx) => (
                  <span
                    key={idx}
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--muted)',
                    }}
                  >
                    {feature.length > 20
                      ? feature.substring(0, 20) + '...'
                      : feature}
                  </span>
                ))}
                {planData.features.length > 3 && (
                  <span
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--muted)',
                    }}
                  >
                    +{planData.features.length - 3} more
                  </span>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleViewInquiry(inquiry)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--text)',
                }}
              >
                <FiEye className="w-3 h-3" />
                View Details
              </button>
              {!inquiry.isArchived ? (
                <button
                  onClick={() => handleArchive(inquiry.id, true)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <FiArchive className="w-3 h-3" />
                  Archive
                </button>
              ) : (
                <button
                  onClick={() => handleArchive(inquiry.id, false)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <FiArchive className="w-3 h-3" />
                  Unarchive
                </button>
              )}
              <button
                onClick={() => handleDelete(inquiry.id)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
                style={{ backgroundColor: '#ef4444', color: 'white' }}
              >
                <FiTrash2 className="w-3 h-3" />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ==================== RENDER ====================
  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>
            Subscription Plan Inquiries
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
            Manage inquiries from clients interested in monthly/yearly
            subscription plans
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80 bg-red-500 text-white"
            >
              <FiTrash2 className="w-4 h-4" />
              <span className="text-sm">
                Delete Selected ({selectedIds.length})
              </span>
            </button>
          )}
          <button
            onClick={() => setShowAnalytics(!showAnalytics)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80 ${showAnalytics ? 'bg-black/10 dark:bg-white/10' : ''}`}
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiTrendingUp className="w-4 h-4" />
            <span className="text-sm">Analytics</span>
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80 ${showFilters ? 'bg-black/10 dark:bg-white/10' : ''}`}
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiFilter className="w-4 h-4" />
            <span className="text-sm">Filters</span>
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiRefreshCw
              className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
            />
            <span className="text-sm">Refresh</span>
          </button>
        </div>
      </div>

      {/* Analytics Dashboard */}
      {showAnalytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in">
          {/* Plan Distribution */}
          <div
            className="p-5 rounded-2xl border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className="font-semibold flex items-center gap-2"
                style={{ color: chartColors.text }}
              >
                <FiStar
                  className="w-4 h-4"
                  style={{ color: chartColors.primary }}
                />
                Plan Distribution
              </h3>
              <span
                className="text-xs"
                style={{ color: chartColors.textSecondary }}
              >
                Total: {stats.total} inquiries
              </span>
            </div>
            {planChartData.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={planChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={props =>
                        renderCustomizedLabel({ ...props, isDark })
                      }
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {planChartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={PIE_COLORS[index % PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: chartColors.tooltipBg,
                        borderColor: chartColors.tooltipBorder,
                        borderRadius: '8px',
                      }}
                      formatter={(value, name) => [`${value} inquiries`, name]}
                      labelStyle={{ color: chartColors.text }}
                    />
                    <Legend
                      formatter={(value, entry, index) => (
                        <span
                          style={{
                            color: chartColors.textSecondary,
                            fontSize: '11px',
                          }}
                        >
                          {value}
                        </span>
                      )}
                      wrapperStyle={{ fontSize: '11px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center">
                <p
                  className="text-sm"
                  style={{ color: chartColors.textSecondary }}
                >
                  No plan data available
                </p>
              </div>
            )}
          </div>

          {/* Billing Cycle Distribution */}
          <div
            className="p-5 rounded-2xl border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className="font-semibold flex items-center gap-2"
                style={{ color: chartColors.text }}
              >
                <FiCalendar
                  className="w-4 h-4"
                  style={{ color: chartColors.cyan }}
                />
                Billing Cycle Preference
              </h3>
              <span
                className="text-xs"
                style={{ color: chartColors.textSecondary }}
              >
                Monthly vs Yearly
              </span>
            </div>
            {billingChartData.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={billingChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {billingChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: chartColors.tooltipBg,
                        borderColor: chartColors.tooltipBorder,
                        borderRadius: '8px',
                      }}
                      formatter={(value, name, props) => {
                        const total = billingChartData.reduce(
                          (sum, item) => sum + item.value,
                          0,
                        );
                        const percentage = ((value / total) * 100).toFixed(1);
                        return [`${value} inquiries (${percentage}%)`, name];
                      }}
                      labelStyle={{ color: chartColors.text }}
                    />
                    <Legend
                      formatter={(value, entry, index) => (
                        <span
                          style={{
                            color: chartColors.textSecondary,
                            fontSize: '11px',
                          }}
                        >
                          {value}
                        </span>
                      )}
                      wrapperStyle={{ fontSize: '11px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center">
                <p
                  className="text-sm"
                  style={{ color: chartColors.textSecondary }}
                >
                  No billing data available
                </p>
              </div>
            )}
            <div
              className="mt-3 pt-2 border-t flex justify-between text-xs"
              style={{
                borderColor: chartColors.grid,
                color: chartColors.textSecondary,
              }}
            >
              <span>
                Total Inquiries:{' '}
                <strong
                  className="font-bold"
                  style={{ color: chartColors.primary }}
                >
                  {stats.total}
                </strong>
              </span>
              <span>
                Conversion Rate:{' '}
                <strong
                  className="font-bold"
                  style={{ color: chartColors.success }}
                >
                  {stats.total > 0
                    ? ((stats.approved / stats.total) * 100).toFixed(1)
                    : 0}
                  %
                </strong>
              </span>
            </div>
          </div>

          {/* Status Distribution */}
          <div
            className="lg:col-span-1 p-5 rounded-2xl border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className="font-semibold flex items-center gap-2"
                style={{ color: chartColors.text }}
              >
                <FiBarChart2
                  className="w-4 h-4"
                  style={{ color: chartColors.secondary }}
                />
                Status Distribution
              </h3>
              <span
                className="text-xs"
                style={{ color: chartColors.textSecondary }}
              >
                Current Status Overview
              </span>
            </div>
            {statusChartData.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {statusChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: chartColors.tooltipBg,
                        borderColor: chartColors.tooltipBorder,
                        borderRadius: '8px',
                      }}
                      formatter={(value, name, props) => {
                        const total = statusChartData.reduce(
                          (sum, item) => sum + item.value,
                          0,
                        );
                        const percentage = ((value / total) * 100).toFixed(1);
                        return [`${value} inquiries (${percentage}%)`, name];
                      }}
                      labelStyle={{ color: chartColors.text }}
                    />
                    <Legend
                      formatter={(value, entry, index) => (
                        <span
                          style={{
                            color: chartColors.textSecondary,
                            fontSize: '11px',
                          }}
                        >
                          {value}
                        </span>
                      )}
                      wrapperStyle={{ fontSize: '11px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center">
                <p
                  className="text-sm"
                  style={{ color: chartColors.textSecondary }}
                >
                  No status data available
                </p>
              </div>
            )}
          </div>

          {/* Timeline Chart */}
          <div
            className="lg:col-span-1 p-5 rounded-2xl border"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3
                className="font-semibold flex items-center gap-2"
                style={{ color: chartColors.text }}
              >
                <FiCalendar
                  className="w-4 h-4"
                  style={{ color: chartColors.cyan }}
                />
                Inquiry Timeline
              </h3>
              <div className="flex gap-1">
                {['30days', '90days', 'lifetime'].map(range => (
                  <button
                    key={range}
                    onClick={() => setTimelineRange(range)}
                    className={`px-3 py-1 rounded-lg text-xs transition-all ${
                      timelineRange === range
                        ? 'bg-black/20 dark:bg-white/20 font-medium'
                        : 'hover:bg-black/5 dark:hover:bg-white/5'
                    }`}
                    style={{ color: chartColors.text }}
                  >
                    {range === '30days'
                      ? 'Last 30 Days'
                      : range === '90days'
                        ? 'Last 90 Days'
                        : 'Lifetime'}
                  </button>
                ))}
              </div>
            </div>
            {chartData.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={chartData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="colorCount"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={chartColors.primary}
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor={chartColors.primary}
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={chartColors.grid}
                    />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                      interval={
                        timelineRange === '30days'
                          ? 5
                          : timelineRange === '90days'
                            ? 14
                            : 30
                      }
                    />
                    <YAxis
                      tick={{ fill: chartColors.textSecondary, fontSize: 11 }}
                      allowDecimals={false}
                    />
                    <Tooltip content={<CustomTooltip isDark={isDark} />} />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke={chartColors.primary}
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorCount)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center">
                <p
                  className="text-sm"
                  style={{ color: chartColors.textSecondary }}
                >
                  No timeline data available
                </p>
              </div>
            )}
            <div
              className="mt-3 pt-2 border-t flex justify-between text-xs"
              style={{ borderColor: chartColors.grid }}
            >
              <span style={{ color: chartColors.textSecondary }}>
                Daily inquiry volume
              </span>
              <span style={{ color: chartColors.textSecondary }}>
                Peak:{' '}
                <strong style={{ color: chartColors.primary }}>
                  {Math.max(...chartData.map(d => d.count), 0)}
                </strong>{' '}
                inquiries
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {filters.map(f => (
          <button
            key={f.id}
            onClick={() => setStatusFilter(f.id)}
            className={`p-3 rounded-xl text-center transition-all duration-200 hover:scale-105 ${
              statusFilter === f.id
                ? 'bg-black/10 dark:bg-white/10'
                : 'border hover:bg-black/5 dark:hover:bg-white/5'
            }`}
            style={{ borderColor: 'var(--border)' }}
          >
            <div className="flex items-center justify-center gap-1 mb-1">
              <f.icon className="w-3 h-3" style={{ color: 'var(--muted)' }} />
              <p className="text-xl font-bold" style={{ color: 'var(--text)' }}>
                {f.count}
              </p>
            </div>
            <p className="text-xs" style={{ color: 'var(--muted)' }}>
              {f.label}
            </p>
          </button>
        ))}
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <div
          className="p-4 rounded-xl border animate-fade-in"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Date Range Filter */}
            <div>
              <label
                className="text-xs font-medium block mb-1"
                style={{ color: 'var(--muted)' }}
              >
                Date Range
              </label>
              <select
                value={dateRange}
                onChange={e => setDateRange(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-all"
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="quarter">Last 90 Days</option>
              </select>
            </div>

            {/* Plan Filter */}
            <div>
              <label
                className="text-xs font-medium block mb-1"
                style={{ color: 'var(--muted)' }}
              >
                Plan
              </label>
              <select
                value={planFilter}
                onChange={e => setPlanFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-all"
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                <option value="all">All Plans</option>
                {availablePlans.map(plan => (
                  <option key={plan} value={plan}>
                    {plan}
                  </option>
                ))}
              </select>
            </div>

            {/* Billing Cycle + Sort */}
            <div>
              <label
                className="text-xs font-medium block mb-1"
                style={{ color: 'var(--muted)' }}
              >
                Billing Cycle
              </label>
              <div className="flex gap-2">
                <select
                  value={billingFilter}
                  onChange={e => setBillingFilter(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  {billingOptions.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => setBillingFilter('all')}
                  className="px-3 py-2 rounded-lg text-xs transition-all hover:opacity-80"
                  style={{
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          {/* Sort Options */}
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-3 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <div>
              <label
                className="text-xs font-medium block mb-1"
                style={{ color: 'var(--muted)' }}
              >
                Sort By
              </label>
              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <option value="date">Date</option>
                  <option value="name">Name</option>
                  <option value="plan">Plan</option>
                  <option value="status">Status</option>
                  <option value="price">Price</option>
                </select>
                <button
                  onClick={() =>
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
                  }
                  className="px-3 py-2 rounded-lg transition-all hover:bg-black/5 dark:hover:bg-white/5"
                  style={{
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  {sortOrder === 'asc' ? (
                    <FiChevronUp className="w-4 h-4" />
                  ) : (
                    <FiChevronDown className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Quick Actions */}
            <div>
              <label
                className="text-xs font-medium block mb-1"
                style={{ color: 'var(--muted)' }}
              >
                Quick Actions
              </label>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => {
                    setDateRange('all');
                    setPlanFilter('all');
                    setBillingFilter('all');
                    setStatusFilter('all');
                    setSearchTerm('');
                  }}
                  className="px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
                  style={{
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  Clear All Filters
                </button>
                <button
                  onClick={() => handleExport('csv')}
                  className="px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
                  style={{
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <FiDownload className="inline w-3 h-3 mr-1" />
                  Export CSV
                </button>
                <button
                  onClick={() => handleExport('json')}
                  className="px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
                  style={{
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <FiFileText className="inline w-3 h-3 mr-1" />
                  Export JSON
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="relative">
        <FiSearch
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
          style={{ color: 'var(--muted)' }}
        />
        <input
          type="text"
          placeholder="Search by name, email, company, plan, status, or billing cycle..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-9 pr-4 py-2 rounded-xl outline-none transition-all"
          style={{
            backgroundColor: 'transparent',
            border: '1px solid var(--border)',
            color: 'var(--text)',
          }}
        />
      </div>

      {/* View Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-1.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-black/10 dark:bg-white/10' : ''}`}
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiGrid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-1.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-black/10 dark:bg-white/10' : ''}`}
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiList className="w-4 h-4" />
          </button>
        </div>
        {inquiries.length > 0 && (
          <span className="text-xs" style={{ color: 'var(--muted)' }}>
            {inquiries.length} inquiries
          </span>
        )}
      </div>

      {/* Select All */}
      {inquiries.length > 0 && (
        <div className="flex items-center gap-3 px-1">
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-2 text-sm transition-all hover:opacity-80"
            style={{ color: 'var(--muted)' }}
          >
            <span
              className="w-4 h-4 rounded border-2 flex items-center justify-center"
              style={{ borderColor: 'var(--border)' }}
            >
              {selectAll && (
                <FiCheck className="w-3 h-3" style={{ color: 'var(--text)' }} />
              )}
            </span>
            {selectAll ? 'Deselect All' : 'Select All'}
            <span className="text-xs" style={{ color: 'var(--muted)' }}>
              ({inquiries.length} items)
            </span>
          </button>
          {selectedIds.length > 0 && (
            <span className="text-xs" style={{ color: 'var(--muted)' }}>
              • {selectedIds.length} selected
            </span>
          )}
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <div
          className="flex items-center justify-between p-3 rounded-xl animate-fade-in flex-wrap gap-2"
          style={{ backgroundColor: 'var(--border)' }}
        >
          <span className="text-sm" style={{ color: 'var(--text)' }}>
            {selectedIds.length} inquiry(ies) selected
          </span>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => handleBulkAction('mark-read')}
              className="px-3 py-1.5 rounded-lg text-sm transition-all hover:opacity-80"
              style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
            >
              Mark Read
            </button>
            <select
              onChange={e => {
                if (e.target.value) {
                  handleBulkAction('set-status', e.target.value);
                  e.target.value = '';
                }
              }}
              className="px-3 py-1.5 rounded-lg text-sm transition-all hover:opacity-80"
              style={{
                backgroundColor: 'var(--bg)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
              }}
              defaultValue=""
            >
              <option value="" disabled>
                Set Status
              </option>
              <option value="in-review">In Review</option>
              <option value="responded">Responded</option>
              <option value="approved">Approved</option>
              <option value="declined">Declined</option>
              <option value="completed">Completed</option>
            </select>
            <button
              onClick={() => handleBulkAction('archive')}
              className="px-3 py-1.5 rounded-lg text-sm transition-all hover:opacity-80"
              style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
            >
              Archive
            </button>
            <button
              onClick={() => handleBulkAction('unarchive')}
              className="px-3 py-1.5 rounded-lg text-sm transition-all hover:opacity-80"
              style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
            >
              Unarchive
            </button>
            <button
              onClick={handleBulkDelete}
              className="px-3 py-1.5 rounded-lg text-sm transition-all hover:opacity-80"
              style={{ backgroundColor: '#ef4444', color: 'white' }}
            >
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Inquiries List/Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div
            className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: 'var(--text)' }}
          />
        </div>
      ) : inquiries.length === 0 ? (
        <div
          className="text-center py-12 rounded-2xl border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <FiCalendar
            className="w-12 h-12 mx-auto mb-3 opacity-50"
            style={{ color: 'var(--muted)' }}
          />
          <p style={{ color: 'var(--muted)' }}>
            No subscription inquiries found
          </p>
          <button
            onClick={() => {
              setStatusFilter('all');
              setBillingFilter('all');
              setPlanFilter('all');
              setDateRange('all');
              setSearchTerm('');
            }}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            Clear All Filters
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {inquiries.map(inquiry => (
            <InquiryCard key={inquiry.id} inquiry={inquiry} />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {inquiries.map(inquiry => (
            <InquiryListItem key={inquiry.id} inquiry={inquiry} />
          ))}
        </div>
      )}

      {/* Load More */}
      {hasMore && !loading && (
        <div className="flex justify-center pt-4">
          <button
            onClick={loadInquiries}
            className="px-6 py-2 rounded-xl text-sm transition-all hover:opacity-80"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            Load More
          </button>
        </div>
      )}

      {/* Inquiry Detail Modal */}
      {selectedInquiry && (
        <SubscriptionInquiryModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedInquiry(null);
          }}
          inquiry={selectedInquiry}
          onStatusUpdate={handleStatusUpdate}
          onDelete={handleDelete}
          onArchive={handleArchive}
        />
      )}
    </div>
  );
}

export default SubscriptionInquiries;
